import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { supabase } from '../../lib/supabase';
import { collegeJourneyService } from '../../lib/college-journey-service';
import { Save, Eye, ArrowRight, ChevronLeft, CheckCircle, User } from 'lucide-react';

const ParentObservations = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, familyUsers, family } = useFamilyContext();
  const { selectedStudent } = useCollegeContext();
  
  // Get student from multiple sources: URL param, selected student, or localStorage
  const getStudent = () => {
    const studentIdFromUrl = searchParams.get('studentId');
    
    // First try familyUsers if available
    if (studentIdFromUrl && familyUsers?.length > 0) {
      const studentFromUrl = familyUsers.find(u => u.id === studentIdFromUrl);
      if (studentFromUrl) return studentFromUrl;
    }
    
    if (selectedStudent) return selectedStudent;
    
    // Try from localStorage (fallback for parent hub selection)
    const savedStudentId = localStorage.getItem('college-selected-student-id');
    if (savedStudentId) {
      // Try familyUsers first
      if (familyUsers?.length > 0) {
        const savedStudent = familyUsers.find(u => u.id === savedStudentId);
        if (savedStudent) return savedStudent;
      }
      
      // If familyUsers is empty, try family.kids
      if (family?.kids?.length > 0) {
        const savedStudent = family.kids.find(kid => kid.id === savedStudentId);
        if (savedStudent) {
          // Transform family.kids format to expected format
          return {
            ...savedStudent,
            type: 'student',
            displayName: savedStudent.name || savedStudent.displayName,
            auth_user_id: savedStudent.authUserId || savedStudent.auth_user_id,
            
          };
        }
      }
    }
    
    // Default to first child from familyUsers or family.kids
    if (familyUsers?.length > 0) {
      return familyUsers.find(user => user.type !== 'parent');
    }
    
    // Fallback to family.kids
    if (family?.kids?.length > 0) {
      const firstKid = family.kids[0];
      return {
        ...firstKid,
        type: 'student',
        displayName: firstKid.name || firstKid.displayName,
        auth_user_id: firstKid.authUserId || firstKid.auth_user_id,
        
      };
    }
    
    return null;
  };
  
  const student = getStudent();
  
  // Save selected student to localStorage for consistency
  useEffect(() => {
    if (student?.id) {
      localStorage.setItem('college-selected-student-id', student.id);
    }
  }, [student?.id]);
  
  const [profile, setProfile] = useState({
    parentObservationEasySubject: '',
    parentObservationGrowth: '',
    parentObservationLearningDifference: '',
    parentObservationSurprises: '',
    parentObservationAgeLimit: '',
    parentObservationRulesQuestioned: '',
    parentObservationWrongLevel: '',
    parentObservationAdvocacySuccess: '',
    parentObservationInitiativeAgainstAdvice: '',
    parentObservationMechanicalSkills: '',
    parentObservationMathIntegration: '',
    parentObservationEntrepreneurialInstincts: '',
    parentObservationObsessions: '',
    parentObservationUnusualInterests: '',
    parentObservationHiddenQualities: ''
  });
  
  const [saveStatus, setSaveStatus] = useState('saved');
  const [saveMessage, setSaveMessage] = useState('');
  const [selectedAcademicAreas, setSelectedAcademicAreas] = useState(['Engineering']);
  const [validUserId, setValidUserId] = useState(null); // Store the user_id that works

  useEffect(() => {
    if (student) {
      loadExistingData();
    }
  }, [student?.id]); // Only run when student ID changes, not the whole object

  // Auto-save when profile changes
  useEffect(() => {
    // Only auto-save if we have a student and some data
    if (!student || !Object.values(profile).some(val => val?.trim())) {
      return;
    }

    const saveTimeout = setTimeout(() => {
      console.log('🔄 ParentObservations: Auto-saving...');
      setSaveStatus('saving');
      setSaveMessage('Auto-saving...');
      handleSave();
    }, 2000); // Save after 2 seconds of inactivity

    return () => clearTimeout(saveTimeout);
  }, [profile, student]); // Dependencies: profile changes and student context

  // Additional periodic save every 30 seconds as backup protection
  useEffect(() => {
    if (!student || !Object.values(profile).some(val => val?.trim())) {
      return;
    }

    const interval = setInterval(() => {
      console.log('🔄 ParentObservations: Periodic save...');
      handleSave();
    }, 30000); // Save every 30 seconds

    return () => clearInterval(interval);
  }, [student]); // Only re-setup when student changes

  const loadExistingData = async () => {
    if (!student) return;
    
    console.log('🔍 ParentObservations: Loading data for student:', student);
    console.log('🔍 ParentObservations: Student fields:', Object.keys(student));
    
    try {
      // Try different possible ID field names for the student
      const possibleIds = [
        student.id,
        student.auth_user_id,
        student.authUserId,
        student.user_id,
        student.userId
      ].filter(Boolean);
      
      console.log('🔍 ParentObservations: Trying IDs:', possibleIds);
      
      let profileData = null;
      
      // Try each ID until we find data
      for (const userId of possibleIds) {
        console.log('🔍 ParentObservations: Searching with user_id:', userId);
        
        const { data, error } = await supabase
          .from('student_academic_profiles')
          .select(`
            parent_observation_easy_subject,
            parent_observation_growth,
            parent_observation_poor_fit,
            parent_observation_learning_difference,
            parent_observation_surprises,
            parent_observation_age_limit,
            parent_observation_rules_questioned,
            parent_observation_wrong_level,
            parent_observation_advocacy_success,
            parent_observation_initiative_against_advice,
            parent_observation_mechanical_skills,
            parent_observation_math_integration,
            parent_observation_entrepreneurial_instincts,
            parent_observation_self_taught,
            parent_observation_obsessions,
            parent_observation_unusual_interests,
            parent_observation_hidden_qualities
          `)
          .eq('user_id', userId);
        
        if (error) {
          console.log('🔍 ParentObservations: Error with user_id', userId, ':', error);
          continue;
        }
        
        console.log('🔍 ParentObservations: Results for user_id', userId, ':', data);
        
        if (data && data.length > 0) {
          profileData = data[0];
          console.log('✅ ParentObservations: Found profile data:', profileData);
          setValidUserId(userId); // Store the user_id that worked
          break;
        }
      }
      
      if (profileData) {
        setProfile({
          parentObservationEasySubject: profileData.parent_observation_easy_subject || '',
          parentObservationGrowth: profileData.parent_observation_growth || '',
          parentObservationLearningDifference: profileData.parent_observation_learning_difference || profileData.parent_observation_poor_fit || '',
          parentObservationSurprises: profileData.parent_observation_surprises || '',
          parentObservationAgeLimit: profileData.parent_observation_age_limit || '',
          parentObservationRulesQuestioned: profileData.parent_observation_rules_questioned || '',
          parentObservationWrongLevel: profileData.parent_observation_wrong_level || '',
          parentObservationAdvocacySuccess: profileData.parent_observation_advocacy_success || '',
          parentObservationInitiativeAgainstAdvice: profileData.parent_observation_initiative_against_advice || '',
          parentObservationMechanicalSkills: profileData.parent_observation_mechanical_skills || '',
          parentObservationMathIntegration: profileData.parent_observation_math_integration || '',
          parentObservationEntrepreneurialInstincts: profileData.parent_observation_entrepreneurial_instincts || '',
          parentObservationObsessions: profileData.parent_observation_obsessions || '',
          parentObservationUnusualInterests: profileData.parent_observation_unusual_interests || '',
          parentObservationHiddenQualities: profileData.parent_observation_hidden_qualities || ''
        });
        
        // Load saved parent-selected academic interest areas
        if (profileData.parent_selected_academic_areas) {
          try {
            const savedAreas = JSON.parse(profileData.parent_selected_academic_areas);
            if (Array.isArray(savedAreas) && savedAreas.every(area => academicAreas.includes(area))) {
              setSelectedAcademicAreas(savedAreas);
            }
          } catch (error) {
            console.log('Error parsing parent-selected academic interest areas:', error);
          }
        } else if (profileData.academic_interest_areas) {
          // Migration fallback for previous field name
          try {
            const savedAreas = JSON.parse(profileData.academic_interest_areas);
            if (Array.isArray(savedAreas) && savedAreas.every(area => academicAreas.includes(area))) {
              setSelectedAcademicAreas(savedAreas);
            }
          } catch (error) {
            console.log('Error parsing academic interest areas:', error);
          }
        } else if (profileData.academic_interest_area && academicAreas.includes(profileData.academic_interest_area)) {
          // Fallback for old single-area format
          setSelectedAcademicAreas([profileData.academic_interest_area]);
        }
      }
    } catch (error) {
      console.error('Error loading parent observations:', error);
    }
  };

  const handleSave = async () => {
    if (!student) {
      setSaveStatus('error');
      setSaveMessage('No student selected');
      return;
    }
    
    setSaveStatus('saving');
    setSaveMessage('Saving observations...');
    
    console.log('💾 ParentObservations: Starting save for student:', student);
    
    try {
      // Use the validUserId that we found during loading, or fall back to discovery
      let userId = validUserId;
      
      if (!userId) {
        // If we don't have a valid user_id stored, try to find one
        const possibleIds = [
          student.auth_user_id,
          student.authUserId,
          student.user_id,
          student.userId,
          student.id // Try this last since it failed before
        ].filter(Boolean);
        userId = possibleIds[0];
      }
      
      console.log('💾 ParentObservations: Using user_id:', userId);
      
      // Use the improved service method that handles existing profiles properly
      await collegeJourneyService.updateAcademicProfile({
        parent_observation_easy_subject: profile.parentObservationEasySubject,
        parent_observation_growth: profile.parentObservationGrowth,
        parent_observation_learning_difference: profile.parentObservationLearningDifference,
        parent_observation_surprises: profile.parentObservationSurprises,
        parent_observation_age_limit: profile.parentObservationAgeLimit,
        parent_observation_rules_questioned: profile.parentObservationRulesQuestioned,
        parent_observation_wrong_level: profile.parentObservationWrongLevel,
        parent_observation_advocacy_success: profile.parentObservationAdvocacySuccess,
        parent_observation_initiative_against_advice: profile.parentObservationInitiativeAgainstAdvice,
        parent_observation_mechanical_skills: profile.parentObservationMechanicalSkills,
        parent_observation_math_integration: profile.parentObservationMathIntegration,
        parent_observation_entrepreneurial_instincts: profile.parentObservationEntrepreneurialInstincts,
        parent_observation_obsessions: profile.parentObservationObsessions,
        parent_observation_unusual_interests: profile.parentObservationUnusualInterests,
        parent_observation_hidden_qualities: profile.parentObservationHiddenQualities,
        parent_selected_academic_areas: JSON.stringify(selectedAcademicAreas) // Save parent-selected academic areas as JSON array
        // Note: This is separate from student-selected 'academic_interests' field, which should carry more weight in assessments
      }, userId);
      
      setSaveStatus('saved');
      setSaveMessage('Observations saved successfully!');
      setTimeout(() => {
        setSaveStatus('saved');
        setSaveMessage('');
      }, 2000);
      
    } catch (error) {
      console.error('Error saving observations:', error);
      setSaveStatus('error');
      setSaveMessage('Failed to save observations');
      setTimeout(() => {
        setSaveStatus('saved');
        setSaveMessage('');
      }, 3000);
    }
  };

  const updateField = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    if (saveStatus === 'saved') {
      setSaveStatus('unsaved');
    }
  };

  // Academic area options
  const academicAreas = [
    'Engineering',
    'Computer & Data Science',
    'Mathematics',
    'Physical & Life Sciences',
    'Psychology & Human Behavior',
    'Social Sciences & Public Policy',
    'Humanities & Philosophy',
    'Business & Finance',
    'Literature & Language',
    'Creative Arts',
    'Health & Human Services',
    'Environment & Global Studies'
  ];

  // Subject-specific question variations
  const getCustomizedQuestions = (academicAreas) => {
    // Use the first selected area as primary, or fall back to Engineering
    const primaryArea = academicAreas.length > 0 ? academicAreas[0] : 'Engineering';
    const practicalSkillsVariations = {
      'Engineering': {
        question: "What practical skills or mechanical abilities does your child have that surprise you?",
        placeholder: "Building, fixing, troubleshooting, understanding how things work, taking apart devices..."
      },
      'Computer & Data Science': {
        question: "What technical skills or computational thinking does your child demonstrate?",
        placeholder: "Programming, debugging, understanding systems, finding patterns in data..."
      },
      'Mathematics': {
        question: "What mathematical reasoning or problem-solving abilities does your child show?",
        placeholder: "Mental math, pattern recognition, geometric thinking, logical reasoning..."
      },
      'Physical & Life Sciences': {
        question: "What scientific observation or experimental skills does your child have?",
        placeholder: "Asking 'why' questions, forming hypotheses, systematic observation..."
      },
      'Psychology & Human Behavior': {
        question: "What insights into human behavior or psychology does your child demonstrate?",
        placeholder: "Reading people, understanding motivations, predicting reactions..."
      },
      'Social Sciences & Public Policy': {
        question: "What understanding of social systems or civic issues does your child show?",
        placeholder: "Questioning policies, understanding societal patterns, civic engagement..."
      },
      'Humanities & Philosophy': {
        question: "What philosophical or analytical thinking does your child display?",
        placeholder: "Deep questions, ethical reasoning, connecting historical patterns..."
      },
      'Business & Finance': {
        question: "What business sense or financial understanding does your child have?",
        placeholder: "Entrepreneurial thinking, understanding money, strategic planning..."
      },
      'Literature & Language': {
        question: "What linguistic abilities or literary insights does your child show?",
        placeholder: "Playing with words, understanding subtext, analyzing stories..."
      },
      'Creative Arts': {
        question: "What artistic abilities or creative expression does your child demonstrate?",
        placeholder: "Visual arts, music, creative problem-solving, aesthetic sense..."
      },
      'Health & Human Services': {
        question: "What care-giving or health-related insights does your child show?",
        placeholder: "Helping others, understanding wellness, empathy in action..."
      },
      'Environment & Global Studies': {
        question: "What environmental awareness or global thinking does your child demonstrate?",
        placeholder: "Sustainability consciousness, understanding interconnections, cultural awareness..."
      }
    };

    const analyticalThinkingVariations = {
      'Engineering': {
        question: "When does your child bring mathematical thinking into unexpected situations or conversations?",
        placeholder: "Times they apply math/logic outside of academic contexts, engineering solutions..."
      },
      'Computer & Data Science': {
        question: "When does your child bring computational or algorithmic thinking to everyday situations?",
        placeholder: "Breaking down problems into steps, finding patterns, optimizing processes..."
      },
      'Mathematics': {
        question: "When does your child apply mathematical concepts to non-math situations?",
        placeholder: "Using statistics in sports, geometric thinking in art, logical reasoning in arguments..."
      },
      'Physical & Life Sciences': {
        question: "When does your child bring scientific thinking or experimentation to everyday life?",
        placeholder: "Testing hypotheses, making observations, questioning how things work..."
      },
      'Psychology & Human Behavior': {
        question: "When does your child analyze behavior patterns or social dynamics?",
        placeholder: "Understanding group dynamics, predicting reactions, analyzing motivations..."
      },
      'Social Sciences & Public Policy': {
        question: "When does your child analyze social systems or political situations?",
        placeholder: "Understanding cause and effect in society, questioning policies, seeing patterns..."
      },
      'Humanities & Philosophy': {
        question: "When does your child bring philosophical or critical thinking to everyday situations?",
        placeholder: "Asking deep questions, making connections across ideas, ethical reasoning..."
      },
      'Business & Finance': {
        question: "When does your child apply business or strategic thinking to situations?",
        placeholder: "Optimizing systems, understanding value creation, strategic planning..."
      },
      'Literature & Language': {
        question: "When does your child bring literary analysis or language skills to life?",
        placeholder: "Understanding subtext in conversations, analyzing stories, wordplay..."
      },
      'Creative Arts': {
        question: "When does your child bring artistic or creative thinking to problem-solving?",
        placeholder: "Creative solutions, aesthetic considerations, artistic expression..."
      },
      'Health & Human Services': {
        question: "When does your child bring health awareness or care-giving instincts to situations?",
        placeholder: "Noticing when others need help, understanding wellness, empathetic responses..."
      },
      'Environment & Global Studies': {
        question: "When does your child bring environmental or global awareness to situations?",
        placeholder: "Sustainability thinking, understanding interconnections, cultural sensitivity..."
      }
    };

    // If two areas are selected, blend the questions to acknowledge both
    if (academicAreas.length === 2) {
      const secondaryArea = academicAreas[1];
      
      return {
        practicalSkills: {
          question: practicalSkillsVariations[primaryArea]?.question || practicalSkillsVariations['Engineering'].question,
          placeholder: `${practicalSkillsVariations[primaryArea]?.placeholder || ''} Also consider skills related to ${secondaryArea.toLowerCase()}...`
        },
        analyticalThinking: {
          question: analyticalThinkingVariations[primaryArea]?.question || analyticalThinkingVariations['Engineering'].question,
          placeholder: `${analyticalThinkingVariations[primaryArea]?.placeholder || ''} Also consider how they apply ${secondaryArea.toLowerCase()} thinking...`
        }
      };
    }
    
    return {
      practicalSkills: practicalSkillsVariations[primaryArea] || practicalSkillsVariations['Engineering'],
      analyticalThinking: analyticalThinkingVariations[primaryArea] || analyticalThinkingVariations['Engineering']
    };
  };

  const customizedQuestions = getCustomizedQuestions(selectedAcademicAreas);

  const parentQuestions = [
    {
      id: 'parentObservationEasySubject',
      question: "What subject does your child find surprisingly easy that others struggle with?",
      placeholder: "E.g., Math concepts click immediately, or they understand complex literature without effort...",
      category: 'Academic & Learning Profile',
      section: 'Academic & Learning Profile'
    },
    {
      id: 'parentObservationGrowth',
      question: "Where have you seen the biggest academic growth or breakthrough?",
      placeholder: "E.g., Struggled with writing until 10th grade, then suddenly started writing beautifully...",
      category: 'Academic & Learning Profile',
      section: 'Academic & Learning Profile'
    },
    {
      id: 'parentObservationLearningDifference',
      question: "When has your child's way of thinking or learning been different from what schools typically expect?",
      placeholder: "Times when their approach didn't match standard methods but still worked...",
      category: 'System Interactions & Edge Cases',
      section: 'System Interactions & Edge Cases'
    },
    {
      id: 'parentObservationSurprises',
      question: "What does your child do that consistently surprises adults (teachers, coaches, family friends)?",
      placeholder: "Behaviors, insights, or abilities that catch adults off guard...",
      category: 'System Interactions & Edge Cases',
      section: 'System Interactions & Edge Cases'
    },
    {
      id: 'parentObservationAgeLimit',
      question: "Has your child ever been told they couldn't do something due to age/grade level, but you suspected they could handle it?",
      placeholder: "Times when age restrictions seemed arbitrary given their capabilities...",
      category: 'System Interactions & Edge Cases',
      section: 'System Interactions & Edge Cases'
    },
    {
      id: 'parentObservationRulesQuestioned',
      question: "What rules or systems has your child questioned that made you think 'actually, they have a point'?",
      placeholder: "Legitimate challenges to authority or conventional wisdom...",
      category: 'System Interactions & Edge Cases',
      section: 'System Interactions & Edge Cases'
    },
    {
      id: 'parentObservationWrongLevel',
      question: "Has your child ever been placed in the 'wrong' level for something, either too easy or too hard?",
      placeholder: "E.g., Reading at college level but placed in grade-level English, or struggled in advanced math despite being capable...",
      category: 'System Interactions & Edge Cases',
      section: 'System Interactions & Edge Cases'
    },
    {
      id: 'parentObservationAdvocacySuccess',
      question: "Describe a time your child advocated for themselves or others and succeeded in changing an outcome.",
      placeholder: "Times when they pushed back against decisions and proved they were right...",
      category: 'System Interactions & Edge Cases',
      section: 'System Interactions & Edge Cases'
    },
    {
      id: 'parentObservationInitiativeAgainstAdvice',
      question: "When has your child taken initiative on something despite being advised against it? What was the outcome?",
      placeholder: "Times they chose their own path despite concerns from parents/teachers...",
      category: 'Natural Tendencies & Uniqueness',
      section: 'Natural Tendencies & Uniqueness'
    },
    {
      id: 'parentObservationMechanicalSkills',
      question: customizedQuestions.practicalSkills.question,
      placeholder: customizedQuestions.practicalSkills.placeholder,
      category: 'Natural Tendencies & Uniqueness',
      section: 'Natural Tendencies & Uniqueness'
    },
    {
      id: 'parentObservationMathIntegration',
      question: customizedQuestions.analyticalThinking.question,
      placeholder: customizedQuestions.analyticalThinking.placeholder,
      category: 'Natural Tendencies & Uniqueness',
      section: 'Natural Tendencies & Uniqueness'
    },
    {
      id: 'parentObservationEntrepreneurialInstincts',
      question: "When has your child shown entrepreneurial instincts or figured out how to create/optimize value in social or economic situations?",
      placeholder: "Times they identified opportunities, created businesses, optimized trades, or applied strategic thinking to social systems...",
      category: 'Natural Tendencies & Uniqueness',
      section: 'Natural Tendencies & Uniqueness'
    },
    {
      id: 'parentObservationObsessions',
      question: "What does your child get obsessed with or spend way too much time on?",
      placeholder: "Areas of intense focus or deep interest...",
      category: 'Natural Tendencies & Uniqueness',
      section: 'Natural Tendencies & Uniqueness'
    },
    {
      id: 'parentObservationUnusualInterests',
      question: "What's something your child cares deeply about that doesn't seem typical for their age/peer group?",
      placeholder: "Interests or values that set them apart from peers...",
      category: 'Natural Tendencies & Uniqueness',
      section: 'Natural Tendencies & Uniqueness'
    },
    {
      id: 'parentObservationHiddenQualities',
      question: "What do you hope college admissions officers will understand about your child that might not show up in grades/scores?",
      placeholder: "Important qualities that traditional metrics don't capture...",
      category: 'Natural Tendencies & Uniqueness',
      section: 'Natural Tendencies & Uniqueness'
    }
  ];

  if (!student) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <User className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Student Found</h3>
          <p className="text-slate-600 mb-4">Please select a student to complete parent observations.</p>
          <button
            onClick={() => navigate('/college')}
            className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800"
          >
            Return to College Hub
          </button>
        </div>
      </div>
    );
  }

  const completedFields = Object.values(profile).filter(value => value.trim()).length;
  const totalFields = parentQuestions.length;
  const progressPercentage = Math.round((completedFields / totalFields) * 100);

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/college')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to College Hub
        </button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Parent A Observations</h1>
            <p className="text-lg text-slate-600 mt-2">
              Strategic insights about <strong>{student.displayName || student.name}</strong> that go beyond grades and test scores
            </p>
          </div>
          
          <div className="text-right">
            <div className="text-2xl font-bold text-slate-900">{progressPercentage}%</div>
            <div className="text-sm text-slate-600">{completedFields} of {totalFields} complete</div>
          </div>
        </div>
        
        {/* Academic Interest Areas Selector */}
        <div className="mt-6 bg-azure-50 border border-azure-200 rounded-xl p-4">
          <div>
            <h3 className="text-sm font-semibold text-azure-900 mb-1">Academic Interest Focus (Parent View)</h3>
            <p className="text-xs text-azure-700 mb-4">
              Select up to 2 academic areas to customize parent observation questions. Note: Student-selected interests (if any) will carry more weight in the final assessment.
            </p>
            
            {/* Selected Areas Display */}
            <div className="flex flex-wrap gap-2 mb-4">
              {selectedAcademicAreas.map((area, index) => (
                <div key={area} className="inline-flex items-center gap-2 bg-azure-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
                  <span className="text-xs bg-azure-500 text-white rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {index + 1}
                  </span>
                  {area}
                  <button
                    onClick={() => {
                      setSelectedAcademicAreas(selectedAcademicAreas.filter(a => a !== area));
                      if (saveStatus === 'saved') {
                        setSaveStatus('unsaved');
                      }
                    }}
                    className="ml-1 text-azure-200 hover:text-white"
                  >
                    ×
                  </button>
                </div>
              ))}
              {selectedAcademicAreas.length === 0 && (
                <div className="text-azure-700 text-sm italic">No areas selected</div>
              )}
            </div>
            
            {/* Add Area Selector */}
            {selectedAcademicAreas.length < 2 && (
              <div className="flex items-center gap-2">
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value && !selectedAcademicAreas.includes(e.target.value)) {
                      setSelectedAcademicAreas([...selectedAcademicAreas, e.target.value]);
                      if (saveStatus === 'saved') {
                        setSaveStatus('unsaved');
                      }
                    }
                  }}
                  className="bg-white border border-azure-300 rounded-lg px-3 py-2 text-sm text-azure-900 focus:outline-none focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                >
                  <option value="">+ Add academic interest area</option>
                  {academicAreas
                    .filter(area => !selectedAcademicAreas.includes(area))
                    .map(area => (
                      <option key={area} value={area}>{area}</option>
                    ))}
                </select>
                <span className="text-xs text-azure-700">
                  ({2 - selectedAcademicAreas.length} more allowed)
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="w-full bg-slate-200 rounded-full h-2 mt-4">
          <div 
            className="bg-slate-900 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* Form */}
      <div className="space-y-8">
        {/* Group questions by section */}
        {['Academic & Learning Profile', 'System Interactions & Edge Cases', 'Natural Tendencies & Uniqueness'].map((sectionName, sectionIndex) => {
          const sectionQuestions = parentQuestions.filter(q => q.section === sectionName);
          const sectionCompleted = sectionQuestions.filter(q => profile[q.id]?.trim()).length;
          
          return (
            <div key={sectionName} className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-slate-900">{sectionName}</h3>
                  <div className="text-sm text-slate-600">
                    {sectionCompleted} of {sectionQuestions.length} completed
                  </div>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
                  <div 
                    className="bg-slate-900 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(sectionCompleted / sectionQuestions.length) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="space-y-6">
                {sectionQuestions.map((question, questionIndex) => {
                  const isCustomized = question.id === 'parentObservationMechanicalSkills' || question.id === 'parentObservationMathIntegration';
                  
                  return (
                    <div key={question.id} className="border-l-4 border-slate-200 pl-6">
                      <div className="flex items-start gap-4">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0 ${
                          profile[question.id]?.trim() ? 'bg-emerald-500' : 'bg-slate-300'
                        }`}>
                          {profile[question.id]?.trim() ? <CheckCircle className="w-3 h-3" /> : questionIndex + 1}
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <label className="block text-sm font-medium text-slate-700">
                              {question.question}
                            </label>
                            {isCustomized && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-azure-100 text-azure-800">
                                Tailored to {selectedAcademicAreas.length === 1 
                                  ? selectedAcademicAreas[0] 
                                  : selectedAcademicAreas.join(' + ')
                                }
                              </span>
                            )}
                          </div>
                          
                          <textarea
                            value={profile[question.id] || ''}
                            onChange={(e) => updateField(question.id, e.target.value)}
                            placeholder={question.placeholder}
                            className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center mt-8 pt-6 border-t border-slate-200">
        <div className="flex items-center gap-3">
          {saveStatus === 'saving' && (
            <div className="flex items-center gap-2 text-slate-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-600"></div>
              <span>Saving...</span>
            </div>
          )}
          {saveStatus === 'saved' && saveMessage && (
            <div className="flex items-center gap-2 text-emerald-600">
              <CheckCircle className="w-4 h-4" />
              <span>{saveMessage}</span>
            </div>
          )}
          {saveStatus === 'error' && (
            <div className="text-red-600">{saveMessage}</div>
          )}
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={handleSave}
            disabled={saveStatus === 'saving'}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium ${
              saveStatus === 'unsaved' 
                ? 'bg-slate-900 text-white hover:bg-slate-800' 
                : saveStatus === 'saving'
                ? 'bg-slate-400 text-white cursor-not-allowed'
                : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
            }`}
          >
            <Save className="w-4 h-4" />
            {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'unsaved' ? 'Save Changes' : 'Saved'}
          </button>
          
          <button
            onClick={() => navigate('/college/parent-observations-b')}
            className="flex items-center gap-2 px-6 py-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium"
          >
            Continue to Parent B
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ParentObservations;